import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { useEffect, useState } from 'react'
import { getSupabaseClient } from '#bv/lib/supabaseClient'

const searchSchema = z.object({
  next: z.string().optional(),
})

export const Route = createFileRoute('/auth/callback')({
  validateSearch: searchSchema,
  component: AuthCallbackPage,
})

function AuthCallbackPage() {
  const navigate = useNavigate()
  const { next } = Route.useSearch()
  const [err, setErr] = useState('')

  useEffect(() => {
    const run = async () => {
      try {
        const supabase = getSupabaseClient()

        // If Supabase is using PKCE, the callback will include ?code=...
        // Exchange it for a session immediately.
        if (typeof window !== 'undefined') {
          const url = new URL(window.location.href)
          const code = url.searchParams.get('code')
          if (code) {
            await supabase.auth.exchangeCodeForSession(code)
            // Remove the auth code from the URL to avoid re-exchanging on reload.
            url.searchParams.delete('code')
            window.history.replaceState(null, '', url.toString())
          }
        }

        const { data: sub } = supabase.auth.onAuthStateChange(() => {
          // session may finalize asynchronously after landing on the callback URL
          void supabase.auth.getSession()
        })

        // Give Supabase a short window to process URL tokens and persist session.
        for (let i = 0; i < 14; i++) {
          const { data } = await supabase.auth.getSession()
          if (data.session?.access_token) {
            sub.subscription.unsubscribe()
            await navigate({ to: (next?.trim() || '/customer') as never, replace: true })
            return
          }
          await new Promise((r) => setTimeout(r, 250))
        }
        sub.subscription.unsubscribe()
        setErr('Login did not complete. Please try again.')
      } catch (e) {
        setErr(e instanceof Error ? e.message : 'Login did not complete. Please try again.')
      }
    }
    void run()
  }, [navigate, next])

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#050508' }}>
      <div
        className="w-full max-w-md rounded-2xl border p-8 shadow-2xl"
        style={{
          borderColor: 'rgba(0,191,255,0.15)',
          background: 'linear-gradient(165deg, rgba(14,10,18,0.98), rgba(9,7,12,0.96))',
          boxShadow: '0 8px 36px rgba(0,0,0,0.45)',
        }}
      >
        <h1 className="font-display text-2xl tracking-wide chrome-text">Signing you in…</h1>
        <p className="mt-2 text-sm" style={{ color: '#5a6d8a' }}>
          Completing your secure session.
        </p>
        {err ? (
          <p className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">{err}</p>
        ) : (
          <div className="mt-5 flex items-center gap-3 text-[#5a6d8a]">
            <span className="h-5 w-5 animate-spin rounded-full border-2 border-[#00bfff] border-t-transparent" />
            Please wait…
          </div>
        )}
      </div>
    </div>
  )
}

